import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from 'barte-design-system';
import { api, NewCommentPayload } from './api';
import { Comment, DraftAnchor, Mode } from './types';
import { canonicalRoute } from './routeCanonical';

const LS_MODE = 'barte-comments-mode';
const LS_AUTHOR = 'barte-comments-author';
const POLL_MS = 8000;
const POLL_MS_HIDDEN = 30000;

interface CommentsContextValue {
  mode: Mode;
  author: string | null;
  route: string;
  routeComments: Comment[];
  allComments: Comment[];
  drawerOpen: boolean;
  activeThreadId: string | null;
  highlightThreadId: string | null;
  draft: DraftAnchor | null;

  openThreadsCurrentRoute: number;

  toggleOnOff: () => void;
  exitCommentMode: () => void;
  setAuthor: (name: string) => void;
  setDraft: (d: DraftAnchor | null) => void;
  openThread: (threadId: string | null) => void;
  highlightThread: (threadId: string | null) => void;
  setDrawerOpen: (open: boolean) => void;

  submitNew: (body: string, author: string, draft: DraftAnchor) => Promise<void>;
  submitReply: (threadId: string, body: string, authorName?: string) => Promise<void>;
  resolveThread: (rootId: string, resolved: boolean) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;

  refreshAll: () => Promise<void>;
  navigateToThread: (threadId: string) => Promise<void>;
}

const Ctx = createContext<CommentsContextValue | null>(null);

export function useComments(): CommentsContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useComments fora do CommentsProvider');
  return v;
}

export function CommentsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  // rota canônica (unifica aliases como /pos e /backoffice/pos)
  const route = canonicalRoute(location.pathname);

  // Ativo ⇒ sempre modo comentário (crosshair). Não há estado "só visualização".
  const [mode, setMode] = useState<Mode>(
    () => (localStorage.getItem(LS_MODE) === 'on' ? 'comment' : 'off')
  );
  const [author, setAuthorState] = useState<string | null>(
    () => localStorage.getItem(LS_AUTHOR)
  );
  const [routeComments, setRouteComments] = useState<Comment[]>([]);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [highlightThreadId, setHighlightThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftAnchor | null>(null);

  const active = mode !== 'off';
  const activeRef = useRef(active);
  activeRef.current = active;
  const routeRef = useRef(route);
  routeRef.current = route;

  // -------- persistência --------
  const setAuthor = useCallback((name: string) => {
    setAuthorState(name);
    localStorage.setItem(LS_AUTHOR, name);
  }, []);

  const toggleOnOff = useCallback(() => {
    setMode((m) => {
      if (m === 'off') {
        // Ativar já entra em modo comentário (cursor crosshair pronto).
        localStorage.setItem(LS_MODE, 'on');
        return 'comment';
      }
      localStorage.setItem(LS_MODE, 'off');
      return 'off';
    });
    setDraft(null);
    setActiveThreadId(null);
  }, []);

  // Fecha o compositor mas PERMANECE em modo comentário (crosshair volta).
  const exitCommentMode = useCallback(() => {
    setDraft(null);
  }, []);

  const notifyError = useCallback(
    (description: string) => {
      toast.error({ title: 'Comentários', description });
    },
    [toast]
  );

  // -------- fetch (leitura silenciosa; só escrita notifica erro) --------
  const fetchRoute = useCallback(async (r: string) => {
    try {
      const list = await api.list(r);
      if (routeRef.current === r) setRouteComments(list);
    } catch (_) {
      /* silencioso — protótipo */
    }
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      setAllComments(await api.all());
    } catch (_) {
      /* silencioso */
    }
  }, []);

  const refreshCurrent = useCallback(async () => {
    await fetchRoute(routeRef.current);
  }, [fetchRoute]);

  // Recarrega ao trocar de rota (quando ativo).
  useEffect(() => {
    if (!active) {
      setRouteComments([]);
      return;
    }
    fetchRoute(route);
  }, [route, active, fetchRoute]);

  // -------- polling com backoff em background --------
  // Poll leve só da rota atual. O painel geral (/all) NÃO é repollado aqui —
  // é carregado ao abrir o drawer e após mutações.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      if (cancelled) return; // evita cadeias órfãs se o cleanup rodou durante o await
      const delay = document.visibilityState === 'hidden' ? POLL_MS_HIDDEN : POLL_MS;
      timer = setTimeout(async () => {
        if (!cancelled && activeRef.current && document.visibilityState !== 'hidden') {
          await refreshCurrent();
        }
        schedule();
      }, delay);
    };
    schedule();
    const onVisible = () => {
      if (document.visibilityState !== 'hidden' && activeRef.current) refreshCurrent();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, refreshCurrent]);

  // Carrega todos ao abrir o drawer.
  useEffect(() => {
    if (drawerOpen && active) refreshAll();
  }, [drawerOpen, active, refreshAll]);

  // -------- mutações (otimistas) --------
  const submitNew = useCallback(
    async (body: string, authorName: string, d: DraftAnchor) => {
      setAuthor(authorName);
      const r = routeRef.current;
      const payload: NewCommentPayload = {
        route: r,
        anchor_selector: d.anchor_selector,
        anchor_offset_x: d.anchor_offset_x,
        anchor_offset_y: d.anchor_offset_y,
        fallback_x: d.fallback_x,
        fallback_y: d.fallback_y,
        author_name: authorName,
        body,
      };
      const tempId = safeUuid();
      const optimistic: Comment = {
        id: tempId,
        thread_id: tempId,
        resolved: false,
        is_root: true,
        created_at: new Date().toISOString(),
        ...payload,
      } as Comment;
      setRouteComments((prev) => [...prev, optimistic]);
      try {
        await api.create(payload);
        setDraft(null); // limpa o compositor só no sucesso (preserva o texto no erro)
      } catch (_) {
        notifyError('Não foi possível enviar o comentário. Tente de novo.');
      } finally {
        await refreshCurrent();
      }
    },
    [setAuthor, refreshCurrent, notifyError]
  );

  const submitReply = useCallback(
    async (threadId: string, body: string, replyAuthor?: string) => {
      const authorName = (replyAuthor || author || '').trim();
      if (!authorName) {
        notifyError('Defina seu nome para responder.');
        return;
      }
      setAuthor(authorName);
      const root = routeComments.find((c) => c.id === threadId);
      const payload: NewCommentPayload = {
        thread_id: threadId,
        route: routeRef.current,
        anchor_selector: root?.anchor_selector ?? null,
        anchor_offset_x: root?.anchor_offset_x ?? null,
        anchor_offset_y: root?.anchor_offset_y ?? null,
        fallback_x: root?.fallback_x ?? 0,
        fallback_y: root?.fallback_y ?? 0,
        author_name: authorName,
        body,
      };
      const tempId = safeUuid();
      const optimistic: Comment = {
        id: tempId,
        thread_id: threadId,
        resolved: false,
        is_root: false,
        created_at: new Date().toISOString(),
        ...payload,
      } as Comment;
      setRouteComments((prev) => [...prev, optimistic]);
      try {
        await api.create(payload);
      } catch (_) {
        notifyError('Não foi possível enviar a resposta. Tente de novo.');
      } finally {
        await refreshCurrent();
      }
    },
    [author, setAuthor, routeComments, refreshCurrent, notifyError]
  );

  const resolveThread = useCallback(
    async (rootId: string, resolved: boolean) => {
      setRouteComments((prev) =>
        prev.map((c) => (c.id === rootId ? { ...c, resolved } : c))
      );
      if (resolved) setActiveThreadId(null);
      try {
        await api.setResolved(rootId, resolved);
      } catch (_) {
        notifyError('Não foi possível atualizar a thread. Tente de novo.');
      } finally {
        await refreshCurrent();
        if (drawerOpen) await refreshAll();
      }
    },
    [refreshCurrent, refreshAll, drawerOpen, notifyError]
  );

  const deleteComment = useCallback(
    async (id: string) => {
      if (!author) {
        notifyError('Defina seu nome antes de excluir.');
        return;
      }
      setRouteComments((prev) =>
        prev.filter((c) => c.id !== id && c.thread_id !== id)
      );
      try {
        await api.remove(id, author);
      } catch (_) {
        notifyError('Não foi possível excluir. Só o autor pode excluir o próprio comentário.');
      } finally {
        await refreshCurrent();
        if (drawerOpen) await refreshAll();
      }
    },
    [author, refreshCurrent, refreshAll, drawerOpen, notifyError]
  );

  // -------- navegação / deep-link --------
  const openThread = useCallback((threadId: string | null) => {
    setActiveThreadId(threadId);
  }, []);

  const highlightThread = useCallback((threadId: string | null) => {
    setHighlightThreadId(threadId);
    if (threadId) {
      window.setTimeout(() => {
        setHighlightThreadId((cur) => (cur === threadId ? null : cur));
      }, 2600);
    }
  }, []);

  const navigateToThread = useCallback(
    async (threadId: string) => {
      let list = allComments;
      if (!list.length) {
        try {
          list = await api.all();
          setAllComments(list);
        } catch (_) {
          /* ignore */
        }
      }
      const root = list.find((c) => c.id === threadId) || list.find((c) => c.thread_id === threadId);
      if (!root) return;
      if (mode === 'off') {
        setMode('comment');
        localStorage.setItem(LS_MODE, 'on');
      }
      setDrawerOpen(false);
      if (root.route !== routeRef.current) {
        navigate(root.route);
      }
      setActiveThreadId(threadId);
      highlightThread(threadId);
    },
    [allComments, mode, navigate, highlightThread]
  );

  const openThreadsCurrentRoute = useMemo(
    () => routeComments.filter((c) => c.is_root && !c.resolved).length,
    [routeComments]
  );

  const value: CommentsContextValue = {
    mode,
    author,
    route,
    routeComments,
    allComments,
    drawerOpen,
    activeThreadId,
    highlightThreadId,
    draft,
    openThreadsCurrentRoute,
    toggleOnOff,
    exitCommentMode,
    setAuthor,
    setDraft,
    openThread,
    highlightThread,
    setDrawerOpen,
    submitNew,
    submitReply,
    resolveThread,
    deleteComment,
    refreshAll,
    navigateToThread,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function safeUuid(): string {
  const c = (window as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'tmp-' + Math.random().toString(16).slice(2) + Date.now().toString(16);
}
